import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Backdrop,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Snackbar,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { Add, Delete, Edit, FilterList, Menu as MenuIcon, Refresh } from '@mui/icons-material';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import PlaylistPlayIcon from '@mui/icons-material/PlaylistPlay';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import TableRowsIcon from '@mui/icons-material/TableRows';
import { DynamicBreadcrumbs } from 'components/Breadcrumb';
import { useModulePermissions } from 'hooks/useModulePermissions';
import { Logger } from 'utils/Logger';
import { playlistsApi } from './Playlists.api';
import { PLAYLISTS_PATH, Playlist, PlaylistUpsertPayload, formatDuration } from './Playlists.types';
import { PlaylistEditorDialog } from './PlaylistEditorDialog';
import { PlaylistPreviewDialog } from './PlaylistPreviewDialog';

const LOG_PREFIX = 'BeamOnScreen/playlists/PlaylistsPage';
const VIEWMODE_STORAGE_KEY = 'playlists-view-mode';

type PlaylistViewMode = 'tile' | 'table';
type PlaylistSortField = 'name' | 'updatedAt' | 'createdAt' | 'reels' | 'duration' | 'screens';
type PlaylistSortOrder = 'asc' | 'desc';

const errorText = (err: unknown, fallback: string) => (err instanceof Error && err.message ? err.message : fallback);
const formatDate = (iso?: string | null) => (iso ? new Date(iso).toLocaleString() : '—');

const comparePlaylists = (a: Playlist, b: Playlist, field: PlaylistSortField, order: PlaylistSortOrder) => {
    const dir = order === 'asc' ? 1 : -1;
    const value = (p: Playlist) => {
        switch (field) {
            case 'name':
                return p.name.toLowerCase();
            case 'createdAt':
                return p.createdAt;
            case 'reels':
                return p.items.length;
            case 'duration':
                return p.totalDurationMs;
            case 'screens':
                return p.screensCount;
            default:
                return p.updatedAt;
        }
    };
    const av = value(a);
    const bv = value(b);
    if (av < bv) return -1 * dir;
    if (av > bv) return 1 * dir;
    return a.name.localeCompare(b.name);
};

/** Beam on Screen → Playlists (`/#/beamonscreen/playlists`). */
export const PlaylistsPage: React.FC<{ title?: string }> = ({ title = 'Playlists' }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { hasBeamonScreenAccess, isUserSuperAdmin } = useModulePermissions();
    const canManage = hasBeamonScreenAccess() || isUserSuperAdmin;

    const heroGradient =
        theme.palette.dashboard?.counterHeroGradient ??
        `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 60%, ${theme.palette.secondary.main} 100%)`;

    const [playlists, setPlaylists] = useState<Playlist[]>([]);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [search, setSearch] = useState('');
    const [sortField, setSortField] = useState<PlaylistSortField>('updatedAt');
    const [sortOrder, setSortOrder] = useState<PlaylistSortOrder>('desc');
    const [viewMode, setViewMode] = useState<PlaylistViewMode>(() => {
        try {
            return localStorage.getItem(VIEWMODE_STORAGE_KEY) === 'table' ? 'table' : 'tile';
        } catch {
            return 'tile';
        }
    });
    const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
    const [mobileActionsOpen, setMobileActionsOpen] = useState(false);

    const [editorOpen, setEditorOpen] = useState(false);
    const [editing, setEditing] = useState<Playlist | null>(null);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<Playlist | null>(null);
    const [previewTarget, setPreviewTarget] = useState<Playlist | null>(null);
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });
    const notify = (message: string, severity: 'success' | 'error' = 'success') => setSnackbar({ open: true, message, severity });

    const changeViewMode = (next: PlaylistViewMode | null) => {
        if (!next) return;
        setViewMode(next);
        try {
            localStorage.setItem(VIEWMODE_STORAGE_KEY, next);
        } catch {
            /* ignore */
        }
    };

    const load = useCallback(async () => {
        setLoading(true);
        setLoadError(null);
        try {
            setPlaylists(await playlistsApi.list());
        } catch (err) {
            setLoadError(errorText(err, 'Could not load playlists'));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void load();
    }, [load]);

    const visible = useMemo(() => {
        const q = search.trim().toLowerCase();
        const filtered = q ? playlists.filter((p) => p.name.toLowerCase().includes(q) || p.description.toLowerCase().includes(q)) : playlists;
        return [...filtered].sort((a, b) => comparePlaylists(a, b, sortField, sortOrder));
    }, [playlists, search, sortField, sortOrder]);

    const openEditor = (playlist: Playlist | null) => {
        setEditing(playlist);
        setSaveError(null);
        setEditorOpen(true);
    };

    const save = async (payload: PlaylistUpsertPayload) => {
        setSaving(true);
        setSaveError(null);
        try {
            const saved = editing ? await playlistsApi.update(editing._id, payload) : await playlistsApi.create(payload);
            setPlaylists((list) => {
                const others = list.filter((p) => p._id !== saved._id);
                return [...others, saved];
            });
            setEditorOpen(false);
            notify(editing ? `Playlist '${saved.name}' saved` : `Playlist '${saved.name}' created`);
        } catch (err) {
            Logger.error(LOG_PREFIX, `save failed: ${err}`);
            setSaveError(errorText(err, 'Could not save the playlist'));
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        if (!deleteTarget) return;
        try {
            const res = await playlistsApi.remove(deleteTarget._id);
            setPlaylists((list) => list.filter((p) => p._id !== deleteTarget._id));
            notify(
                res.screensUnassigned
                    ? `Playlist deleted; ${res.screensUnassigned} screen(s) no longer have a playlist`
                    : 'Playlist deleted',
            );
        } catch (err) {
            notify(errorText(err, 'Could not delete the playlist'), 'error');
        } finally {
            setDeleteTarget(null);
        }
    };

    /** Same small contained icon buttons the Reel Maker list uses for its row actions. */
    const actionButtonSx = { borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 } as const;

    const renderRowActions = (playlist: Playlist) => (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'nowrap', justifyContent: 'flex-end' }} onClick={(e) => e.stopPropagation()}>
            <Tooltip title="Preview">
                <span>
                    <Button
                        color="info"
                        variant="contained"
                        size="small"
                        aria-label={`Preview ${playlist.name}`}
                        disabled={playlist.items.length === 0}
                        onClick={() => setPreviewTarget(playlist)}
                        sx={actionButtonSx}
                    >
                        <PlayCircleOutlineIcon fontSize="small" />
                    </Button>
                </span>
            </Tooltip>
            <Tooltip title={canManage ? 'Edit' : 'View'}>
                <Button
                    color="secondary"
                    variant="contained"
                    size="small"
                    aria-label={`Edit ${playlist.name}`}
                    onClick={() => openEditor(playlist)}
                    sx={actionButtonSx}
                >
                    <Edit fontSize="small" />
                </Button>
            </Tooltip>
            {canManage && (
                <Tooltip title="Delete">
                    <Button
                        color="error"
                        variant="contained"
                        size="small"
                        aria-label={`Delete ${playlist.name}`}
                        onClick={() => setDeleteTarget(playlist)}
                        sx={actionButtonSx}
                    >
                        <Delete fontSize="small" />
                    </Button>
                </Tooltip>
            )}
        </Box>
    );

    const filterControls = (fullWidth: boolean) => (
        <>
            <FormControl sx={fullWidth ? undefined : { minWidth: 160 }} size="small" fullWidth={fullWidth}>
                <InputLabel>Sort By</InputLabel>
                <Select value={sortField} label="Sort By" onChange={(e) => setSortField(e.target.value as PlaylistSortField)}>
                    <MenuItem value="name">Name</MenuItem>
                    <MenuItem value="updatedAt">Last Updated On</MenuItem>
                    <MenuItem value="createdAt">Created On</MenuItem>
                    <MenuItem value="reels">Reels</MenuItem>
                    <MenuItem value="duration">Duration</MenuItem>
                    <MenuItem value="screens">Screens</MenuItem>
                </Select>
            </FormControl>
            <FormControl sx={fullWidth ? undefined : { minWidth: 100 }} size="small" fullWidth={fullWidth}>
                <InputLabel>Order</InputLabel>
                <Select value={sortOrder} label="Order" onChange={(e) => setSortOrder(e.target.value as PlaylistSortOrder)}>
                    <MenuItem value="asc">ASC</MenuItem>
                    <MenuItem value="desc">DSC</MenuItem>
                </Select>
            </FormControl>
        </>
    );

    const viewToggle = (
        <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_e, next) => changeViewMode(next)}
            size="small"
            sx={{ '& .MuiToggleButton-root': { border: '1px solid', borderColor: 'divider' } }}
        >
            <ToggleButton value="tile" aria-label="Tile view">
                <Tooltip title="Tile View">
                    <ViewModuleIcon fontSize="small" />
                </Tooltip>
            </ToggleButton>
            <ToggleButton value="table" aria-label="Table view">
                <Tooltip title="Table View">
                    <TableRowsIcon fontSize="small" />
                </Tooltip>
            </ToggleButton>
        </ToggleButtonGroup>
    );

    return (
        <Box pl={{ xs: 1.25, md: 2.5 }} pt={1} width="100%" mb={3}>
            {loading && (
                <Backdrop sx={{ color: theme.palette.secondary.main, zIndex: theme.zIndex.drawer + 1 }} open={loading}>
                    <CircularProgress color="inherit" />
                </Backdrop>
            )}

            <DynamicBreadcrumbs
                items={[
                    { itemPath: '/dashboard', itemLabel: 'Home' },
                    { itemPath: PLAYLISTS_PATH, itemLabel: title },
                ]}
            />
            {canManage && (
                <Box sx={{ display: { xs: 'flex', md: 'none' }, justifyContent: 'center', mb: 2, px: { xs: 0.5, sm: 1 } }}>
                    <Button
                        variant="outlined"
                        color="secondary"
                        startIcon={<Add />}
                        onClick={() => openEditor(null)}
                        fullWidth
                        sx={{
                            maxWidth: 480,
                            py: 1.25,
                            fontWeight: 700,
                            letterSpacing: '0.04em',
                            borderWidth: 2,
                            textTransform: 'uppercase',
                            '&:hover': { borderWidth: 2 },
                        }}
                    >
                        New
                    </Button>
                </Box>
            )}

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: { xs: 1, md: 2 },
                    alignItems: 'center',
                    mb: 3,
                    p: 1.5,
                    borderRadius: 2,
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0.06)',
                }}
            >
                <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center' }}>
                    <IconButton
                        color="secondary"
                        onClick={() => setMobileFiltersOpen(true)}
                        size="large"
                        aria-label="Search and filters"
                        sx={{ border: '1px solid', borderColor: 'secondary.main' }}
                    >
                        <FilterList />
                    </IconButton>
                </Box>
                <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', ml: 'auto' }}>
                    <IconButton
                        color="secondary"
                        onClick={() => setMobileActionsOpen(true)}
                        size="large"
                        aria-label="Toolbar actions"
                        sx={{ border: '1px solid', borderColor: 'secondary.main' }}
                    >
                        <MenuIcon />
                    </IconButton>
                </Box>

                <Box sx={{ display: { xs: 'none', md: 'block' }, flexGrow: 1 }}>
                    <TextField
                        label="Search Playlist"
                        variant="outlined"
                        size="small"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        sx={{ flexGrow: 1, minWidth: 200 }}
                    />
                </Box>
                <Box sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'row', gap: 1, flexWrap: 'wrap' }}>
                    {filterControls(false)}
                </Box>
                <Box sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'row', gap: 1, alignItems: 'center' }}>
                    {viewToggle}
                    <Tooltip title="Refresh">
                        <span>
                            <Button
                                variant="contained"
                                color="secondary"
                                size="small"
                                aria-label="Refresh playlists"
                                onClick={() => void load()}
                                disabled={loading}
                                sx={{ minWidth: 36, px: 1, flexShrink: 0 }}
                            >
                                <Refresh fontSize="small" />
                            </Button>
                        </span>
                    </Tooltip>
                    {canManage && (
                        <Button variant="contained" color="secondary" startIcon={<Add />} onClick={() => openEditor(null)} size="small" sx={{ whiteSpace: 'nowrap' }}>
                            New
                        </Button>
                    )}
                </Box>
            </Box>

            <Dialog open={mobileFiltersOpen} onClose={() => setMobileFiltersOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Search & Filters</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField label="Search Playlist" size="small" value={search} onChange={(e) => setSearch(e.target.value)} fullWidth />
                        {filterControls(true)}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setMobileFiltersOpen(false)} variant="outlined" color="secondary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={mobileActionsOpen} onClose={() => setMobileActionsOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle>Actions</DialogTitle>
                <DialogContent>
                    <Box display="flex" alignItems="center" gap={1} mt={1}>
                        <Typography variant="body2" sx={{ mr: 1 }}>
                            View:
                        </Typography>
                        {viewToggle}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setMobileActionsOpen(false)} variant="outlined" color="secondary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {loadError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {loadError}
                </Alert>
            )}

            {!loading && visible.length === 0 && (
                <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
                    <PlaylistPlayIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {playlists.length === 0 ? 'No playlists yet' : 'No playlists match your search'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {playlists.length === 0 && canManage ? 'Click NEW to create your first playlist.' : 'Try a different search.'}
                    </Typography>
                </Paper>
            )}

            {viewMode === 'tile' && visible.length > 0 && (
                <Box display="flex" flexWrap="wrap" gap={2.5} sx={{ justifyContent: { xs: 'center', lg: 'flex-start' } }}>
                    {visible.map((playlist) => {
                        const problems = playlist.items.filter((i) => i.missing || i.reelStatus === 'deleted').length;
                        return (
                            <Box
                                key={playlist._id}
                                onClick={() => openEditor(playlist)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        openEditor(playlist);
                                    }
                                }}
                                sx={{
                                    width: 300,
                                    height: isMobile ? 220 : 200,
                                    borderRadius: 2.5,
                                    overflow: 'hidden',
                                    position: 'relative',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                    cursor: 'pointer',
                                    flexShrink: 0,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    '&:hover .tile-overlay': { height: isMobile ? 168 : 132 },
                                    '&:hover .tile-details': { opacity: 1 },
                                    '&:hover .tile-name-short': { opacity: 0, height: 0, lineHeight: 0 },
                                    '&:focus-visible': { outline: `2px solid ${theme.palette.secondary.main}`, outlineOffset: 2 },
                                }}
                            >
                                <Box sx={{ position: 'absolute', inset: 0, zIndex: 0, background: heroGradient, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <PlaylistPlayIcon sx={{ fontSize: 72, color: 'rgba(255,255,255,0.85)' }} />
                                </Box>
                                <Box sx={{ position: 'absolute', top: 8, left: 8, right: 8, zIndex: 2, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                    <Chip
                                        label={`${playlist.items.length} reels`}
                                        color="secondary"
                                        size="small"
                                        sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700, boxShadow: '0 1px 4px rgba(0,0,0,0.3)' }}
                                    />
                                    <Chip
                                        label={formatDuration(playlist.totalDurationMs)}
                                        size="small"
                                        sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700, fontFamily: 'monospace', color: '#fff', backgroundColor: 'rgba(0,0,0,0.55)', boxShadow: '0 1px 4px rgba(0,0,0,0.3)' }}
                                    />
                                    <Chip
                                        label={`${playlist.screensCount} screens`}
                                        size="small"
                                        sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700, color: '#fff', backgroundColor: 'rgba(0,0,0,0.55)', boxShadow: '0 1px 4px rgba(0,0,0,0.3)' }}
                                    />
                                    {problems > 0 && (
                                        <Chip label={`${problems} missing`} color="warning" size="small" sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700 }} />
                                    )}
                                </Box>
                                <Box
                                    className="tile-overlay"
                                    sx={{
                                        position: 'absolute',
                                        bottom: 0,
                                        left: 0,
                                        right: 0,
                                        zIndex: 3,
                                        overflow: 'hidden',
                                        background: 'linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.72) 50%, rgba(0,0,0,0.25) 100%)',
                                        transition: 'height 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                                        height: isMobile ? 168 : 40,
                                        px: 1.5,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'flex-start',
                                    }}
                                >
                                    <Typography
                                        variant="subtitle2"
                                        noWrap
                                        className="tile-name-short"
                                        sx={{
                                            color: '#fff',
                                            fontWeight: 700,
                                            fontSize: '0.82rem',
                                            lineHeight: '40px',
                                            textShadow: '0 1px 3px rgba(0,0,0,0.4)',
                                            flexShrink: 0,
                                            display: isMobile ? 'none' : 'block',
                                            transition: 'opacity 0.2s ease, height 0.2s ease, line-height 0.2s ease',
                                        }}
                                    >
                                        {playlist.name}
                                    </Typography>
                                    <Box
                                        className="tile-details"
                                        sx={{
                                            opacity: isMobile ? 1 : 0,
                                            transition: 'opacity 0.3s ease 0.08s',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 0.5,
                                            pt: 0.75,
                                            pb: 1,
                                            flex: 1,
                                        }}
                                    >
                                        <Typography variant="subtitle2" noWrap title={playlist.name} sx={{ color: '#fff', fontWeight: 700, fontSize: '0.78rem', lineHeight: 1.3 }}>
                                            {playlist.name}
                                        </Typography>
                                        <Typography variant="caption" noWrap title={playlist.description} sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.68rem', lineHeight: 1.2 }}>
                                            {playlist.description || 'No description'}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.68rem', lineHeight: 1.2 }}>
                                            Updated: {new Date(playlist.updatedAt).toLocaleDateString()}
                                        </Typography>
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 'auto' }}>{renderRowActions(playlist)}</Box>
                                    </Box>
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            )}

            {viewMode === 'table' && visible.length > 0 && (
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1 }}>
                    <Table size={isMobile ? 'small' : 'medium'}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Description</TableCell>
                                <TableCell align="right">Reels</TableCell>
                                <TableCell>Duration</TableCell>
                                <TableCell align="right">Screens</TableCell>
                                <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Revision</TableCell>
                                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Updated</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {visible.map((playlist) => {
                                const problems = playlist.items.filter((i) => i.missing || i.reelStatus === 'deleted').length;
                                return (
                                    <TableRow key={playlist._id} hover sx={{ cursor: 'pointer' }} onClick={() => openEditor(playlist)}>
                                        <TableCell sx={{ fontWeight: 600 }}>{playlist.name}</TableCell>
                                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, maxWidth: 360 }}>
                                            <Typography variant="body2" noWrap title={playlist.description}>
                                                {playlist.description || '—'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            {playlist.items.length}
                                            {problems > 0 && <Chip size="small" color="warning" label={`${problems} missing`} sx={{ ml: 1 }} />}
                                        </TableCell>
                                        <TableCell>{formatDuration(playlist.totalDurationMs)}</TableCell>
                                        <TableCell align="right">{playlist.screensCount}</TableCell>
                                        <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>{playlist.revision}</TableCell>
                                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{formatDate(playlist.updatedAt)}</TableCell>
                                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                            {renderRowActions(playlist)}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <PlaylistEditorDialog
                open={editorOpen}
                playlist={editing}
                saving={saving}
                error={saveError}
                canManage={canManage}
                onClose={() => setEditorOpen(false)}
                onSave={(payload) => void save(payload)}
            />

            <PlaylistPreviewDialog
                open={Boolean(previewTarget)}
                title={previewTarget?.name ?? ''}
                entries={(previewTarget?.items ?? [])
                    .filter((i) => !i.missing)
                    .map((i) => ({ reelId: i.reelId, version: i.version, reelName: i.reelName, versionLabel: i.versionLabel }))}
                onClose={() => setPreviewTarget(null)}
            />

            <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Delete playlist?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        '{deleteTarget?.name}' will be deleted.
                        {deleteTarget && deleteTarget.screensCount > 0
                            ? ` ${deleteTarget.screensCount} screen(s) currently play it and will be left without a playlist.`
                            : ''}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteTarget(null)} variant="outlined" color="secondary">
                        Cancel
                    </Button>
                    <Button color="error" variant="contained" startIcon={<Delete />} onClick={() => void remove()}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default PlaylistsPage;
